import { useState, useRef, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { Case, SalesforceStatusInfo } from '../../types';
import { StatusPill, Button } from '../ui';
import { formatDate } from '../../utils/dateFormat';



// Spinner component for loading states
const Spinner = ({ size = 'sm' }: { size?: 'sm' | 'md' }) => {
  const sizeClasses = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  return (
    <span
      className={`inline-block ${sizeClasses} border-2 border-purple-400 border-t-transparent rounded-full animate-spin`}
    />
  );
};

interface CaseTableProps {
  cases: Case[];
  showCheckbox?: boolean;
  showActions?: boolean;
  onTranslate?: (caseNumber: string) => void;
  onDownload?: (caseNumber: string) => void;
  onApprove?: (caseNumber: string) => void;
  onUpload?: (caseNumber: string) => void;
  onRestart?: (caseNumber: string) => void;
  selectedCases?: Set<string>;
  onSelectCase?: (caseNumber: string, selected: boolean) => void;
  onSelectAll?: (selected: boolean) => void;
  columns?: ('checkbox' | 'caseNumber' | 'typeOfAnalysis' | 'pages' | 'dateModified' | 'updatedAt' | 'status' | 'sfStatus' | 'sfLastModified' | 'actions')[];
  maxHeight?: string;
  // Job status functions
  isDownloading?: (caseNumber: string) => boolean;
  isTranslating?: (caseNumber: string) => boolean;
  isUploading?: (caseNumber: string) => boolean;
  isRestarting?: (caseNumber: string) => boolean;
  // Ephemeral Salesforce status data (keyed by case_number)
  sfStatusMap?: Record<string, SalesforceStatusInfo>;
}

export function CaseTable({
  cases,
  showCheckbox = false,
  showActions = true,
  onTranslate,
  onDownload,
  onApprove: _onApprove,
  onUpload,
  onRestart,
  selectedCases = new Set(),
  onSelectCase,
  onSelectAll,
  columns = ['caseNumber', 'typeOfAnalysis', 'status', 'actions'],
  maxHeight = '440px',
  isDownloading,
  isTranslating,
  isUploading,
  isRestarting,
  sfStatusMap = {},
}: CaseTableProps) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Drag selection state
  const isDragging = useRef(false);
  const dragStartIndex = useRef<number | null>(null);
  const dragSelectMode = useRef<boolean>(true); // true = selecting, false = deselecting

  const handleRowMouseDown = useCallback((index: number, caseNumber: string, e: React.MouseEvent) => {
    // Ignore if clicking on interactive elements
    if ((e.target as HTMLElement).closest('a, button, input')) return;

    e.preventDefault();
    isDragging.current = true;
    dragStartIndex.current = index;

    // Determine if we're selecting or deselecting based on current state
    const isCurrentlySelected = selectedCases.has(caseNumber);
    dragSelectMode.current = !isCurrentlySelected;

    // Toggle the clicked row
    onSelectCase?.(caseNumber, !isCurrentlySelected);
  }, [selectedCases, onSelectCase]);

  const handleRowMouseEnter = useCallback((_index: number, caseNumber: string) => {
    if (!isDragging.current || dragStartIndex.current === null) return;

    // Select/deselect this row based on drag mode
    onSelectCase?.(caseNumber, dragSelectMode.current);
  }, [onSelectCase]);

  // Add global mouseup listener to handle drag end outside table
  useEffect(() => {
    const handleMouseUp = () => {
      isDragging.current = false;
      dragStartIndex.current = null;
    };

    document.addEventListener('mouseup', handleMouseUp);
    return () => document.removeEventListener('mouseup', handleMouseUp);
  }, []);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const allSelected = cases.length > 0 && cases.every((c) => selectedCases.has(c.case_number));

  const sortedCases = [...cases].sort((a, b) => {
    if (!sortKey) return 0;
    const aVal = (a as any)[sortKey] || '';
    const bVal = (b as any)[sortKey] || '';
    const comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    return sortDir === 'asc' ? comparison : -comparison;
  });

  const renderActions = (caseItem: Case) => {
    const { status, case_number, is_translating } = caseItem;

    // Check if this case is being processed by a background job
    const downloading = isDownloading?.(case_number) ?? false;
    const translating = isTranslating?.(case_number) ?? is_translating ?? false;
    const uploading = isUploading?.(case_number) ?? false;
    const restarting = isRestarting?.(case_number) ?? false;

    return (
      <div className="flex gap-1 items-center flex-wrap">
        {/* Download action for pending cases */}
        {status === 'pending' && onDownload && (
          downloading ? (
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-xs px-2 py-1">
              <Spinner size="sm" />
              <span>Downloading...</span>
            </div>
          ) : (
            <Button variant="success" size="sm" onClick={() => onDownload(case_number)} className="text-xs px-2 py-1">
              Download
            </Button>
          )
        )}

        {/* Translate action */}
        {(status === 'downloaded' || status === 'translating') && onTranslate && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => onTranslate(case_number)}
            disabled={translating}
            isLoading={translating}
            className="min-w-[70px] bg-[#4682B4] hover:bg-[#4073A0] text-xs px-2 py-1"
          >
            Translate
          </Button>
        )}

        {/* Restart action for returned/found cases */}
        {['returned', 'found'].includes(status) && onRestart && (
          restarting ? (
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-xs px-2 py-1">
              <Spinner size="sm" />
              <span>Restarting...</span>
            </div>
          ) : (
            <Button
              variant="danger"
              size="sm"
              onClick={() => onRestart(case_number)}
              disabled={restarting}
              className="text-xs px-2 py-1"
            >
              Restart
            </Button>
          )
        )}

        {/* Review action */}
        {['ready_for_review', 'translated', 'generating_pdf', 'translating', 'downloaded', 'approved', 'uploaded', 'failed', 'returned', 'lost', 'found', 'other'].includes(status) && (
          <Link to={`/review/${case_number}`}>
            <Button variant="secondary" size="sm" className="bg-purple-200 hover:bg-purple-300 text-gray-800 text-xs px-2 py-1">
              Review
            </Button>
          </Link>
        )}

        {/* Upload action */}
        {(status === 'approved' || status === 'uploading') && onUpload && (
          uploading ? (
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-xs px-2 py-1">
              <Spinner size="sm" />
              <span>Uploading...</span>
            </div>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onUpload(case_number)}
              disabled={uploading}
              className="text-xs px-2 py-1"
            >
              Upload
            </Button>
          )
        )}
      </div>
    );
  };

  const renderSortHeader = (key: string, label: string) => (
    <th
      className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-gray-300"
      onClick={() => handleSort(key)}
    >
      <span className="flex items-center gap-1">
        {label}
        <span
          className={`inline-block w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent transition-all ${
            sortKey === key
              ? sortDir === 'asc'
                ? 'border-b-[5px] border-b-purple-500'
                : 'border-t-[5px] border-t-purple-500'
              : 'border-t-[5px] border-t-gray-400 dark:border-t-gray-500'
          }`}
        />
      </span>
    </th>
  );

  if (cases.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500 dark:text-gray-400">
        No cases found.
      </div>
    );
  }

  return (
    <div className="overflow-auto" style={{ maxHeight }} data-onboarding="case-table">
      <table className="w-full border-collapse table-fixed">
        <thead className="bg-gray-50 dark:bg-dark-recessed sticky top-0 z-10 shadow-sm">
          <tr>
            {showCheckbox && columns.includes('checkbox') && (
              <th className="w-8 px-2 py-3 text-center">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) => onSelectAll?.(e.target.checked)}
                  className="w-4 h-4 accent-purple-500 cursor-pointer"
                />
              </th>
            )}
            {columns.includes('caseNumber') && (
              <th className="w-24 px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Case
              </th>
            )}
            {columns.includes('typeOfAnalysis') && (
              <th
                className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-gray-300"
                onClick={() => handleSort('type_of_analysis')}
              >
                <span className="flex items-center gap-1">
                  Type
                  <span
                    className={`inline-block w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent transition-all ${
                      sortKey === 'type_of_analysis'
                        ? sortDir === 'asc'
                          ? 'border-b-[5px] border-b-purple-500'
                          : 'border-t-[5px] border-t-purple-500'
                        : 'border-t-[5px] border-t-gray-400 dark:border-t-gray-500'
                    }`}
                  />
                </span>
              </th>
            )}
            {columns.includes('pages') && (
              <th
                className="w-16 px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-gray-300"
                onClick={() => handleSort('page_count')}
              >
                <span className="flex items-center gap-1">
                  Pgs
                  <span
                    className={`inline-block w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent transition-all ${
                      sortKey === 'page_count'
                        ? sortDir === 'asc'
                          ? 'border-b-[5px] border-b-purple-500'
                          : 'border-t-[5px] border-t-purple-500'
                        : 'border-t-[5px] border-t-gray-400 dark:border-t-gray-500'
                    }`}
                  />
                </span>
              </th>
            )}
            {columns.includes('dateModified') &&
              renderSortHeader('last_modified', 'Date Modified')}
            {columns.includes('updatedAt') &&
              renderSortHeader('updated_at', 'Updated')}
            {columns.includes('status') && (
              <th
                className="w-28 px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-gray-300"
                onClick={() => handleSort('status')}
              >
                <span className="flex items-center gap-1">
                  Status
                  <span
                    className={`inline-block w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent transition-all ${
                      sortKey === 'status'
                        ? sortDir === 'asc'
                          ? 'border-b-[5px] border-b-purple-500'
                          : 'border-t-[5px] border-t-purple-500'
                        : 'border-t-[5px] border-t-gray-400 dark:border-t-gray-500'
                    }`}
                  />
                </span>
              </th>
            )}
            {columns.includes('sfStatus') &&
              renderSortHeader('salesforce_status', 'SF Status')}
            {columns.includes('sfLastModified') && (
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                SF Modified
              </th>
            )}
            {showActions && columns.includes('actions') && (
              <th className="w-40 px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Actions
              </th>
            )}
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-dark-elevated divide-y divide-gray-200 dark:divide-dark-border">
          {sortedCases.map((caseItem, index) => (
            <tr
              key={caseItem.id}
              className={`hover:bg-gray-50 dark:hover:bg-dark-hover transition-colors ${
                selectedCases.has(caseItem.case_number)
                  ? 'bg-purple-50 dark:bg-purple-900/20'
                  : ''
              } ${showCheckbox ? 'cursor-pointer select-none' : ''}`}
              onMouseDown={showCheckbox ? (e) => handleRowMouseDown(index, caseItem.case_number, e) : undefined}
              onMouseEnter={showCheckbox ? () => handleRowMouseEnter(index, caseItem.case_number) : undefined}
            >
              {showCheckbox && columns.includes('checkbox') && (
                <td className="px-2 py-3 text-center">
                  <input
                    type="checkbox"
                    checked={selectedCases.has(caseItem.case_number)}
                    onChange={(e) =>
                      onSelectCase?.(caseItem.case_number, e.target.checked)
                    }
                    className="w-4 h-4 accent-purple-500 cursor-pointer"
                  />
                </td>
              )}
              {columns.includes('caseNumber') && (
                <td className="px-2 py-3">
                  <a
                    href={`https://spanside.lightning.force.com/lightning/r/Case/${caseItem.salesforce_id}/view`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm font-medium underline inline-flex items-center"
                  >
                    {caseItem.case_number}
                    
                  </a>
                </td>
              )}
              {columns.includes('typeOfAnalysis') && (
                <td className="px-2 py-3 text-gray-700 dark:text-gray-300 text-sm truncate">
                  {caseItem.type_of_analysis || caseItem.record_type || 'N/A'}
                </td>
              )}
              {columns.includes('pages') && (
                <td className="px-2 py-3 text-gray-700 dark:text-gray-300 text-sm">
                  {caseItem.page_count || '-'}
                </td>
              )}
              {columns.includes('dateModified') && (
                <td className="px-2 py-3 text-gray-700 dark:text-gray-300 text-sm">
                  {formatDate(caseItem.last_modified)}
                </td>
              )}
              {columns.includes('updatedAt') && (
                <td className="px-2 py-3 text-gray-700 dark:text-gray-300 text-sm">
                  {formatDate(caseItem.updated_at)}
                </td>
              )}
              {columns.includes('status') && (
                <td className="px-2 py-3">
                  <div className="max-w-full overflow-hidden">
                    <StatusPill status={caseItem.status} />
                  </div>
                </td>
              )}
              {columns.includes('sfStatus') && (
                <td className="px-2 py-3">
                  <div className="max-w-full overflow-hidden">
                    <StatusPill
                      status={
                        sfStatusMap[caseItem.case_number]?.salesforce_status ||
                        caseItem.salesforce_status ||
                        'N/A'
                      }
                    />
                  </div>
                </td>
              )}
              {columns.includes('sfLastModified') && (
                <td className="px-2 py-3 text-gray-700 dark:text-gray-300 text-sm">
                  {formatDate(
                    sfStatusMap[caseItem.case_number]?.last_modified ||
                    caseItem.sf_last_modified ||
                    caseItem.last_modified
                  )}
                </td>
              )}
              {showActions && columns.includes('actions') && (
                <td className="px-2 py-3">{renderActions(caseItem)}</td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
